import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';

/**
 * Map routes to their expected persona names (case-insensitive)
 * These are the persona-specific routes that require persona validation
 */
const routeToPersonaMap: Record<string, string[]> = {
  '/growers': ['grower', 'growers'],
  '/transloading': ['transloader', 'transloading'],
  '/receiver': ['receiver'],
  '/shunting': ['shunter', 'shunting'],
  '/scheduler': ['scheduler'],
  '/gatehouse': ['gatehouse', 'gate house'],
  '/admin': ['admin'],
  '/audit': ['audit', 'auditor']
};

/**
 * Persona Guard - Checks if user has the required persona in localStorage
 * For persona-specific routes, verifies the persona exists before allowing access
 * If persona doesn't exist, redirects to unauthorized page
 */
export const personaGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const currentRoute = state.url.split('?')[0]; // Remove query params
  
  // Check if this is a persona route
  const expectedPersonas = routeToPersonaMap[currentRoute];
  if (!expectedPersonas) {
    // Not a persona route, allow access (authGuard will handle authentication)
    return true;
  }
  
  // Get user's available roles/personas from localStorage
  const storedRolesRaw = localStorage.getItem('roles') || '[]';
  let storedRoles: string[] = [];
  try {
    const parsed = JSON.parse(storedRolesRaw);
    storedRoles = Array.isArray(parsed) ? parsed.map((r: string) => (r || '').toLowerCase()) : [];
  } catch {
    storedRoles = [];
  }
  
  // Fallback: if no roles array, check single persona value
  const currentPersona = localStorage.getItem('persona')?.toLowerCase() || '';
  if (storedRoles.length === 0 && currentPersona) {
    storedRoles = [currentPersona];
  }
  
  if (storedRoles.length === 0) {
    console.log(`🚫 No roles/personas found in localStorage for route: ${currentRoute}`);
    router.navigate(['/unauthorized']);
    return false;
  }
  
  // Check if user has ANY of the required personas in their roles
  const hasPersona = expectedPersonas.some(expectedPersona => 
    storedRoles.some(role => role.includes(expectedPersona.toLowerCase()))
  );
  
  if (!hasPersona) {
    console.log(`🚫 User roles [${storedRoles.join(', ')}] do not match required personas [${expectedPersonas.join(', ')}] for route: ${currentRoute}`);
    router.navigate(['/unauthorized']);
    return false;
  }
  
  // IMPORTANT: Update localStorage persona to match the current route
  // This ensures that if user navigates directly via URL, localStorage is synced with the route
  const routePersona = expectedPersonas[0]; // Use the first (primary) persona name for the route
  if (currentPersona !== routePersona.toLowerCase()) {
    console.log(`🔄 Updating localStorage persona from '${currentPersona}' to '${routePersona}' to match route ${currentRoute}`);
    localStorage.setItem('persona', routePersona);
  }
  
  console.log(`✅ Persona check passed: User has one of the required personas [${expectedPersonas.join(', ')}] for route ${currentRoute}`);
  return true;
};

